import React, { useEffect } from 'react';
import cn from 'classnames';
import { useStaticQuery, graphql } from 'gatsby';
import { useForm } from 'react-hook-form';

import { FORM_CATEGORIES, FORM_TEXTS } from './constants';
import * as styles from './Form.module.css';
import { formStateMachineDefinition } from './formStateMachine';

import useStateMachine from '../../hooks/useStateMachine';

import { GOALS, trackGoal } from '../../utils/fathom';
import { encodeFormData } from '../../utils/string';

import Button from '../Button';

const FormStep = ({
  label,
  helpText,
  children,
  className,
  visible = false
}) => {
  return (
    <div
      aria-hidden={!visible}
      className={cn(styles.formStep, className)}
      style={{
        display: !visible ? 'none' : null
      }}
      transition={{ duration: 0.5 }}>
      <header>
        {label && <label className={cn(styles.label)}>{label}</label>}
        {helpText && <p className={cn(styles.helpText)}>{helpText}</p>}
      </header>

      <main className={cn(styles.inputFields)}>{children}</main>
    </div>
  );
};

// The hidden form is needed for netlify to correctly pick up the form on this
// page. It needs to be included in the server side rendered page.
export const hiddenForm = (
  <form
    name="contact"
    data-netlify="true"
    data-netlify-honeypot="bot-field"
    hidden>
    <input type="hidden" name="form-name" value="contact" required />
    <input type="text" name="category" required />
    <input type="text" name="name" required />
    <input type="test" name="email" required />
    <input type="text" name="message" required />
    <input type="text" name="bot-field" />
  </form>
);

export default function Form() {
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          email
        }
      }
    }
  `);

  const { email } = site.siteMetadata;
  const [formStateMachine, formStateMachineTransition] = useStateMachine(
    formStateMachineDefinition
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting, touchedFields },
    setValue: setFormValue,
    watch: watchFormValue,
    setFocus
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      category: '',
      message: '',
      name: '',
      email: ''
    }
  });

  const category = watchFormValue('category');
  const message = watchFormValue('message');

  const pickCategory = (category) => {
    trackGoal(category.fathomId, 0);
    setFormValue('category', category.key);
    formStateMachineTransition('next');
  };

  const onSubmit = (data) => {
    trackGoal(GOALS.SUBMIT_CONTACT_FORM);
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encodeFormData({ 'form-name': 'contact', ...data })
    })
      .then((resp) => {
        if (resp.status === 200) {
          setTimeout(() => formStateMachineTransition('sent'), 500);
        } else {
          setTimeout(() => formStateMachineTransition('error'), 500);
        }
      })
      .catch(() => formStateMachineTransition('error'));
  };

  // Setting the focus when transitioning to a new step of the form, so it's
  // easier for the user to fill everything.
  useEffect(() => {
    if (formStateMachine.value === 'message') setFocus('message');
    if (formStateMachine.value === 'details') setFocus('name');
  }, [formStateMachine]);

  return (
    <>
      <FormStep
        label="What can we help you with?"
        helpText="Thanks for reaching out. We’re always happy to hear from you. Please select a category below."
        visible={formStateMachine.value === 'category'}>
        <div className={cn(styles.buttonList)}>
          {FORM_CATEGORIES.map((category) => (
            <Button
              size="small"
              key={category.key}
              label={category.label}
              handleClick={() => pickCategory(category)}
            />
          ))}
        </div>
      </FormStep>

      <FormStep
        label="Your message"
        helpText={
          FORM_TEXTS.message.helpText[category] ||
          FORM_TEXTS.message.helpText.default
        }
        visible={formStateMachine.value === 'message'}>
        <textarea
          {...register('message', { required: 'Please enter a message' })}
          className={cn(styles.textarea, {
            [styles.filled]: touchedFields.message && !errors.message
          })}
          placeholder={
            FORM_TEXTS.message.placeholder[category] ||
            FORM_TEXTS.message.placeholder.default
          }
          rows={3}></textarea>
        <div className={cn(styles.formStepActions)}>
          <button
            onClick={() => formStateMachineTransition('back')}
            className={cn(styles.formStepBackLink)}>
            Back
          </button>
          <Button
            label="Next"
            size="small"
            disabled={errors.message || message === ''}
            handleClick={() => {
              formStateMachineTransition('next');
            }}
          />
        </div>
      </FormStep>
      <FormStep
        label="Your details"
        helpText={
          FORM_TEXTS.details.helpText[category] ||
          FORM_TEXTS.details.helpText.default
        }
        visible={formStateMachine.value === 'details'}>
        <input
          type="text"
          placeholder="Your name"
          className={cn(styles.input, {
            [styles.filled]: touchedFields.name && !errors.name
          })}
          {...register('name', { required: 'Please tell us your name' })}
        />
        <input
          type="email"
          placeholder="Your email"
          className={cn(styles.input, {
            [styles.filled]: touchedFields.email && !errors.email
          })}
          {...register('email', {
            required: 'Please enter your email',
            pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
          })}
        />
        {touchedFields.email && errors.email && (
          <p className={cn(styles.errorText)}>
            Please provide a valid email address.
          </p>
        )}

        <div className={cn(styles.formStepActions)}>
          <button
            onClick={() => formStateMachineTransition('back')}
            className={cn(styles.formStepBackLink)}>
            Back
          </button>
          <Button
            label={isSubmitting ? 'Sending' : 'Send along'}
            size="small"
            handleClick={handleSubmit(onSubmit)}
            disabled={!isValid || isSubmitting}
          />
        </div>
      </FormStep>
      <FormStep visible={formStateMachine.value === 'success'}>
        <p className={cn(styles.successText)}>
          {FORM_TEXTS.success.helpText[category] ||
            FORM_TEXTS.success.helpText.default}
        </p>
      </FormStep>
      <FormStep
        label="Something went wrong."
        helpText="We’re sorry, but something went wrong sending your message. But don’t worry, you can just contact us using email directly."
        visible={formStateMachine.value === 'error'}>
        <Button
          label={email}
          href={`mailto:${email}`}
          handleClick={() => trackGoal(GOALS.CLICK_MAILTO_LINK)}
        />
      </FormStep>
    </>
  );
}
